import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"

import { useUserRoles } from "./use-user-roles"
import {
  useFetchIntegrationsQuery,
  useFetchOrgDetailsQuery
} from "../../redux/queries"
import { getOrgBrandsIDs } from "../utils"
import { IntegrationType } from "../../types"

export type DashboardMode =
  | "organizations"
  | "full-service"
  | "api"
  | "loan-officer"
  | "none"

/**
 * Integration types that put a BankingBridge UI on the customer's own site.
 * An org with API access and none of these is an API customer: their
 * dashboard should lead with pricing volume, not embeds and email stats.
 */
const EMBED_TYPES: IntegrationType[] = [
  "widget",
  "standalone",
  "standalone_pricing",
  "button",
  "chatbot"
]

/**
 * Which dashboard the home route should render. Derived rather than stored:
 * role decides between admin / brand admin / loan officer, and for brand
 * admins the org's integration mix decides between full service and API.
 * Refine the API signal here if sales ever wants to set it per org.
 */
export const useDashboardMode = () => {
  const { user, isSuperAdmin, isBankAdmin, isLoanOfficer } = useUserRoles()
  const orgId = user?.org?.id ?? ""

  // ?dashboard=api|full-service lets a brand admin preview the other mode
  // without changing their org. Only those two: the override never grants a
  // view the role would not otherwise have.
  const [searchParams] = useSearchParams()
  const requested = searchParams.get("dashboard")
  const override: DashboardMode | null =
    isBankAdmin &&
    !isSuperAdmin &&
    (requested === "api" || requested === "full-service")
      ? requested
      : null

  const needsOrg = isBankAdmin && !isSuperAdmin && !!orgId && !override

  const orgQuery = useFetchOrgDetailsQuery({ id: orgId }, { skip: !needsOrg })
  const orgDetails = orgQuery.data

  const integrationsQuery = useFetchIntegrationsQuery(
    {
      org_id: orgDetails?.id,
      brand_id: getOrgBrandsIDs(orgDetails),
      limit: 9999
    },
    { skip: !needsOrg || !orgDetails?.id }
  )
  const integrations = integrationsQuery.data

  const mode = useMemo<DashboardMode>(() => {
    if (isSuperAdmin) return "organizations"
    if (isBankAdmin) {
      if (override) return override
      if (!orgDetails?.api_access_active) return "full-service"
      const hasEmbeds = (integrations?.results ?? []).some(integration =>
        EMBED_TYPES.includes(integration.type)
      )
      return hasEmbeds ? "full-service" : "api"
    }
    if (isLoanOfficer) return "loan-officer"
    return "none"
  }, [
    isSuperAdmin,
    isBankAdmin,
    isLoanOfficer,
    override,
    orgDetails,
    integrations
  ])

  // Pending only until the first answer, success or failure. RTK Query
  // refetches a failed query for every new subscriber and reports isSuccess and
  // isError both false while it does. Keying on those flags put Home back on
  // the skeleton, which unmounted the dashboard, whose remount refetched again:
  // an endless request loop. `data` and `error` survive a refetch.
  // "Pending" rather than RTK's isLoading: a skipped or not-yet-started query
  // reports isLoading false, which would flash the wrong dashboard.
  const orgPending =
    needsOrg && orgQuery.data === undefined && orgQuery.error === undefined
  const integrationsPending =
    needsOrg &&
    !!orgDetails?.api_access_active &&
    integrationsQuery.data === undefined &&
    integrationsQuery.error === undefined

  return { mode, isLoading: orgPending || integrationsPending }
}
